import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
  useFieldArray,
  useFormState,
  useWatch,
  type Control,
  type UseFormClearErrors,
  type UseFormGetValues,
  type UseFormSetValue,
  type UseFormTrigger,
} from 'react-hook-form';

import { type PartnerOfferActionKind } from '@/api/routes/partners/partners.offers.api';
import Button from '@/components/Button';
import Chip from '@/components/Chip';
import { IconInfoCircle } from '@/components/icon/tabler/IconInfoCircle';
import { IconPlus } from '@/components/icon/tabler/IconPlus';
import OfferActionCard from '@/domain/offers/components/OfferActionCard';
import OfferActionIcon from '@/domain/offers/components/OfferActionIcon';
import { type OfferFormValues, type OfferPreset } from '@/domain/offers/types';
import {
  OFFER_ACTION_KINDS,
  OFFER_LIMITS,
  OFFER_PRESETS,
  applyOfferPreset,
  canAddOfferAction,
  emptyOfferAction,
  fitOfferLocations,
  getOfferPreset,
  offerActionKindHint,
  offerActionKindLabel,
  offerPresetLabel,
} from '@/domain/offers/utils';
import { useTranslate } from '@/state/i18n';

interface Props {
  control: Control<OfferFormValues>;
  setValue: UseFormSetValue<OfferFormValues>;
  getValues: UseFormGetValues<OfferFormValues>;
  trigger: UseFormTrigger<OfferFormValues>;
  clearErrors: UseFormClearErrors<OfferFormValues>;
}

const SET_OPTIONS = { shouldDirty: true } as const;
const STRUCTURE_FIELDS = ['actions', 'locations'] as const;

/**
 * "Botões": quick starts that prefill coupon, buttons and locations, then up to 2 buttons, each
 * with only its own fields. `lead` and `directions` can be added once.
 */
const OfferActionsFields = ({ control, setValue, getValues, trigger, clearErrors }: Props) => {
  const t = useTranslate('domain.offers.components.OfferActionsFields');
  const { fields, append, remove, swap } = useFieldArray({ control, name: 'actions' });
  const actions = useWatch({ control, name: 'actions' }) ?? [];
  const couponEnabled = useWatch({ control, name: 'benefit.enabled' });
  const { errors, isSubmitted } = useFormState({ control, name: 'actions' });
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const currentPreset = getOfferPreset({ benefit: { enabled: couponEnabled }, actions });
  const listError = errors.actions?.root?.message ?? errors.actions?.message;

  /** Buttons and locations depend on each other ("Como chegar" needs a place). */
  const refreshStructureErrors = () => {
    if (isSubmitted) void trigger([...STRUCTURE_FIELDS]);
    else clearErrors([...STRUCTURE_FIELDS]);
  };

  const applyPreset = (preset: OfferPreset) => {
    const next = applyOfferPreset(getValues(), preset);
    setValue('benefit', next.benefit, SET_OPTIONS);
    setValue('actions', next.actions, SET_OPTIONS);
    setValue('locations', next.locations, SET_OPTIONS);
    clearErrors('benefit');
    refreshStructureErrors();
  };

  /** "Como chegar" brings an empty place; without it, blank places go away. */
  const fitLocations = () => {
    const locations = getValues('locations');
    const next = fitOfferLocations(locations, getValues('actions'));
    if (next !== locations) setValue('locations', next, SET_OPTIONS);
  };

  const addAction = (kind: PartnerOfferActionKind) => {
    setMenuAnchor(null);
    append(emptyOfferAction(kind));
    fitLocations();
    refreshStructureErrors();
  };

  const removeAction = (index: number) => {
    remove(index);
    fitLocations();
    refreshStructureErrors();
  };

  return (
    <Stack gap={2.5}>
      <Stack gap={1}>
        <Typography variant="caption" color="text.secondary">
          {t('presetsTitle')}
        </Typography>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {OFFER_PRESETS.map((preset) => {
            const selected = currentPreset === preset;
            return (
              <Chip
                key={preset}
                size="small"
                aria-pressed={selected}
                label={offerPresetLabel[preset]}
                color={selected ? 'dark' : 'neutral'}
                variant={selected ? 'solid' : 'outline'}
                onClick={() => applyPreset(preset)}
              />
            );
          })}
        </Stack>
      </Stack>

      {fields.length ? (
        <Stack gap={1.5}>
          {fields.map((field, index) => (
            <OfferActionCard
              key={field.id}
              control={control}
              index={index}
              kind={field.kind}
              onRemove={() => removeAction(index)}
              onMoveUp={index > 0 ? () => swap(index, index - 1) : undefined}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('empty')}
        </Typography>
      )}

      {listError ? <FormHelperText error>{listError}</FormHelperText> : null}

      <Box>
        <Button
          variant="outlined"
          startIcon={<IconPlus />}
          disabled={!canAddOfferAction(actions)}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
        >
          {t('add')}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
          {t('limit', { count: OFFER_LIMITS.actionsMax })}
        </Typography>
      </Box>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {OFFER_ACTION_KINDS.map((kind) => {
          const allowed = canAddOfferAction(actions, kind);
          return (
            <MenuItem key={kind} disabled={!allowed} onClick={() => addAction(kind)} sx={sx.option}>
              <ListItemIcon>
                <OfferActionIcon kind={kind} fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={offerActionKindLabel[kind]}
                secondary={allowed ? offerActionKindHint[kind] : t('alreadyAdded')}
                slotProps={{ secondary: { sx: sx.optionHint } }}
              />
            </MenuItem>
          );
        })}
      </Menu>

      <Stack direction="row" gap={1} alignItems="flex-start">
        <IconInfoCircle fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} />
        <Typography variant="caption" color="text.secondary">
          {t('attribution')}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default OfferActionsFields;

const sx = {
  option: { alignItems: 'flex-start', py: 1.25, maxWidth: 360 },
  optionHint: { whiteSpace: 'normal' },
};
